/**
 * Field-services portal module wiring: menu entries for My shifts, clients, quotes, jobs, bookings, invoices,
 * crew pay, crew, equipment, incidents, rate card and terms, the crew badge count, and the phone-first app for
 * crew logins. Shown only when the client's portal.modules include
 * them (see lib/config hasModule); core screens never carry this wording.
 */
import { hashParams, useHash } from "../lib/route";
import { listCrew } from "../lib/data";
import type { IconName } from "../components/Icon";
import type { Counts, CountKey, RouteDef } from "../lib/nav";
import type { Profile } from "../lib/supabase";
import { missingTickets, rosteredCrew, TICKETS } from "../lib/tickets";
import { Invoices } from "./billing/Invoices";
import { BILLING_NAV } from "./billing/nav";
import { Clients } from "./clients/Clients";
import { CLIENTS_NAV } from "./clients/nav";
import { Equipment } from "./crew-records/Records";
import { Incidents } from "./field/Incidents";
import { MyShiftsScreen } from "./field/MyShiftsScreen";
import { FIELD_NAV } from "./field/nav";
import { CrewPay } from "./pay/CrewPay";
import { PAY_NAV } from "./pay/nav";
import { Bookings } from "../screens/Bookings";
import { Crew } from "../screens/Crew";
import { CrewApp } from "../screens/CrewApp";
import { PackView } from "../screens/CrewPack";
import { Jobs } from "../screens/Jobs";
import { Quotes } from "../screens/Quotes";
import { RateCard } from "../screens/RateCard";
import { Terms } from "../screens/Terms";
import { BRAND } from "../lib/config";

/** Office preview of a shift's job pack (#/pack/<booking id>), as crew see it. */
function PackPreview() {
  const [id] = hashParams(useHash());
  return (
    <section className="page" aria-labelledby="page-title" style={{ maxWidth: 760 }}>
      {id ? <PackView key={id} bookingId={id} backHref="#/jobs" backLabel="Jobs" /> : <p>No shift chosen.</p>}
    </section>
  );
}

/** The first menu entry after Today: My shifts, for staff whose login is linked to a crew record (App.tsx). */
export const FIELD_ROUTES_FIRST = {
  "#/my": { label: FIELD_NAV.myShifts.label, icon: "hardhat", Screen: MyShiftsScreen, hint: FIELD_NAV.myShifts.hint, module: "my" },
} satisfies Record<string, RouteDef>;

/** Menu entries before CRM. The crew badge counts crew rostered without a ticket they need. */
export const FIELD_ROUTES_MAIN = {
  "#/clients": { label: CLIENTS_NAV.label, icon: "mail", Screen: Clients, hint: CLIENTS_NAV.hint, module: "clients" },
  "#/quotes": { label: "Quotes", icon: "document", Screen: Quotes, module: "quotes" },
  "#/jobs": { label: "Jobs", icon: "file", Screen: Jobs, hint: "Shifts, crew, documents", module: "jobs" },
  "#/bookings": { label: "Bookings", icon: "calendar", Screen: Bookings, module: "bookings" },
  "#/invoices": { label: BILLING_NAV.label, icon: "dollar", Screen: Invoices, hint: BILLING_NAV.hint, module: "invoices" },
  "#/pay": { label: PAY_NAV.label, icon: "dollar", Screen: CrewPay, hint: PAY_NAV.hint, module: "pay" },
  "#/crew": { label: "Crew & tickets", icon: "hardhat", Screen: Crew, count: "missingTickets", module: "crew" },
  "#/equipment": { label: "Equipment", icon: "file", Screen: Equipment, hint: "Kits and checks", module: "equipment" },
  "#/incidents": { label: FIELD_NAV.incidents.label, icon: "warning", Screen: Incidents, hint: FIELD_NAV.incidents.hint, module: "incidents" },
} satisfies Record<string, RouteDef>;

/** Menu entries after CRM, and hidden routes. */
export const FIELD_ROUTES_MORE = {
  "#/rates": { label: "Rate card", icon: "dollar", Screen: RateCard, module: "rates" },
  "#/terms": { label: BRAND.termsTitle, icon: "check", Screen: Terms, hint: "Signed agreements", module: "terms" },
} satisfies Record<string, RouteDef>;

export const FIELD_ROUTES_HIDDEN = {
  "#/pack": { label: "Crew view", icon: "hardhat", Screen: PackPreview, hidden: true, module: "jobs" },
} satisfies Record<string, RouteDef>;

export const FIELD_COUNT_LABEL: Partial<Record<CountKey, (n: number) => string>> = {
  missingTickets: FIELD_NAV.missingTicketsBadge,
};

/** Load the module's badge counts (crew rostered without a ticket they need). Failures leave the badge out. */
export function loadFieldCounts(has: (m: NonNullable<RouteDef["module"]>) => boolean, set: (f: (c: Counts) => Counts) => void): void {
  if (!has("crew") || !TICKETS.length) return;
  Promise.all([listCrew(), rosteredCrew()]).then(([crew, rostered]) => set((c) => ({ ...c, missingTickets: missingTickets(crew, rostered).length })), () => undefined);
}

/** Crew logins (role "controller") get the phone-first job pack app instead of the office portal. */
export const usesFieldApp = (profile: Profile | null) => profile?.role === "controller";
export { CrewApp as FieldApp };

/** Sign-in note when the crew module is on and the client set none of its own (portal.signInNote). */
export function fieldSignIn(has: (m: NonNullable<RouteDef["module"]>) => boolean): { note: string; icon: IconName } | null {
  return has("crew") ? { note: "**Staff and crew only.** Clients and contractors: portals coming soon.", icon: "hardhat" } : null;
}
